import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, InlineLoading, MenuItem, Tooltip } from '@carbon/react';
import { Checkmark } from '@carbon/react/icons';
import { useStore } from '@tanstack/react-form';
import { SuccessToast } from 'components/success-toast';
import ErrorToast from 'components/error-toast';
import { useEval3Page } from './context';
import {
  useCalcDetail,
  useSaveEvaluation,
  type WorkingConfigForm,
} from './hooks';
import { interestItemsDiverged } from './utils';
import styles from './save-evaluation.module.scss';

/** One transient toast descriptor (`key` re-fires the self-dismissing toast on repeats). */
interface ToastState {
  kind: 'success' | 'error';
  message: string;
  key: number;
}

/**
 * The resolved Save-action state, decoupled from its presentation so the same logic drives
 * BOTH the wide primary `<Button>` ({@link SaveEvaluationButton}) and the collapsed
 * `<MenuItem>` ({@link SaveEvaluationMenuItem}) on narrow screens. The success/error toast
 * lives HERE (in whichever component calls the controller hook) rather than on the trigger —
 * so when the trigger is a menu item that unmounts as the menu closes on click, the toast
 * still shows (the caller renders `toast` at a persistently-mounted level).
 */
export interface SaveEvaluationController {
  /** Eval-scoped: false in the package/workspace/saved-evals browse views (hide Save entirely). */
  visible: boolean;
  /** Enabled only for a completed, unsaved, clean calc with no save in flight. */
  canSave: boolean;
  /** Why Save is disabled (actionable reasons only), or null. */
  disabledReason: string | null;
  isSaving: boolean;
  saved: boolean;
  /** The trigger label: 'Saving…' | 'Saved' | 'Save'. */
  label: string;
  /** Fire the save (no-op when there's no calc). */
  onSave: () => void;
  /** The success/error toast node — render at a persistent level so it survives a menu closing. */
  toast: ReactNode;
}

/**
 * Owns the Save action's state + the save mutation + the transient toast, independent of how
 * the trigger is rendered (P8 · Save). Callers render one trigger — {@link SaveEvaluationButton}
 * (wide) or {@link SaveEvaluationMenuItem} (collapsed) — plus `controller.toast` in a
 * persistently-mounted spot.
 *
 * Enabled ONLY when a completed calc exists, it isn't already saved, no save is in flight, AND
 * the working-config form is clean — Save persists the last *computed* result, so a dirty form
 * (uncommitted config edits) disables it, guaranteeing the saved snapshot matches the screen.
 * The form resets to clean after every successful Calculate, so the gate re-enables on recalc.
 * Interest-modeling parity is checked against the COMMITTED blob rather than form meta (the
 * mid-run rebaseline can eat the meta while keeping edited values — see
 * `interestItemsDiverged`), so folded interest edits also hold Save off until recalculated.
 *
 * @param {WorkingConfigForm} workingConfig - The lifted working-config form (for the dirty gate).
 * @return {SaveEvaluationController} The resolved Save state + handler + toast node.
 */
export function useSaveEvaluationController(
  workingConfig: WorkingConfigForm
): SaveEvaluationController {
  const { calculationId, isDraft } = useEval3Page();
  const { data: calcDetail } = useCalcDetail(calculationId);
  const { mutate: save, isPending: isSaving } = useSaveEvaluation();

  // A dirty form = uncommitted config edits not in any calc yet; Save only locks in the last
  // COMPLETED calc, so disable until clean (single source of truth — the form store).
  const isDirty = useStore(workingConfig.form.store, (state) => state.isDirty);
  // Interest-modeling edits can outlive the form meta: the mid-run `form.reset`
  // rebaseline (onRunCompleted) keeps their values but eats the dirty meta, so a
  // meta-only gate would let Save persist a snapshot that doesn't match the screen.
  // Derive their dirtiness from the committed baseline instead.
  const interestItems = useStore(
    workingConfig.form.store,
    (state) => state.values.interestItems
  );
  const interestItemsDirty = interestItemsDiverged(
    interestItems,
    calcDetail?.configurations?.interest_items
  );

  const [toast, setToast] = useState<ToastState | null>(null);
  const showToast = useCallback((kind: ToastState['kind'], message: string) => {
    setToast((prev) => ({ kind, message, key: (prev?.key ?? 0) + 1 }));
  }, []);

  // Clear any lingering toast when the viewed eval changes — the persistent error toast
  // (timeout 0) would otherwise stay up after navigating to a different eval.
  useEffect(() => setToast(null), [calculationId]);

  const handleSave = useCallback(() => {
    if (calculationId === null) {
      return;
    }
    save(calculationId, {
      onSuccess: () => showToast('success', 'Evaluation saved.'),
      onError: (err) =>
        showToast('error', err.message || 'Couldn’t save evaluation.'),
    });
  }, [calculationId, save, showToast]);

  // Eval-scoped: no Save in the package/workspace/saved-evals browse views (calc null AND not a
  // draft). A draft shows Save disabled (no completed calc yet), matching the mock.
  const visible = !(calculationId === null && !isDraft);

  const saved = calcDetail?.saved ?? false;
  const completed = calcDetail?.status === 'Completed';
  const canSave =
    calculationId !== null &&
    completed &&
    !saved &&
    !isSaving &&
    !isDirty &&
    !interestItemsDirty;

  // Explain WHY Save is disabled (only the actionable reasons — not while saving, and not once
  // saved, which the "Saved" label already conveys).
  let disabledReason: string | null = null;
  if (!canSave && !saved && !isSaving) {
    if (calculationId === null || !completed) {
      disabledReason = 'Calculate the evaluation first';
    } else if (isDirty || interestItemsDirty) {
      disabledReason = 'Recalculate to save your latest changes';
    }
  }

  const label = isSaving ? 'Saving…' : saved ? 'Saved' : 'Save';

  const toastNode = toast ? (
    toast.kind === 'success' ? (
      <SuccessToast key={toast.key} message={toast.message} timeout={10_000} />
    ) : (
      <ErrorToast
        key={toast.key}
        message={toast.message}
        timeout={0}
        onClose={() => {
          setToast(null);
          return true;
        }}
      />
    )
  ) : null;

  return {
    visible,
    canSave,
    disabledReason,
    isSaving,
    saved,
    label,
    onSave: handleSave,
    toast: toastNode,
  };
}

/**
 * The wide primary Save `<Button>` (the ≥1500px title-bar treatment). Shows an inline
 * "Saving…" loader, a disabled "Saved" (Checkmark), or "Save"; a disabled button surfaces
 * its reason via a `Tooltip` over a focusable span (a disabled button swallows hover).
 *
 * @param {{ controller: SaveEvaluationController }} props - The resolved Save state.
 * @return {JSX.Element} The Save button (optionally tooltip-wrapped).
 */
export function SaveEvaluationButton({
  controller,
}: {
  controller: SaveEvaluationController;
}): JSX.Element {
  const { canSave, disabledReason, isSaving, saved, onSave } = controller;
  const saveButton = (
    <Button
      kind="primary"
      size="md"
      disabled={!canSave}
      renderIcon={saved && !isSaving ? Checkmark : undefined}
      onClick={onSave}
    >
      {isSaving ? (
        <InlineLoading description="Saving…" />
      ) : saved ? (
        'Saved'
      ) : (
        'Save'
      )}
    </Button>
  );

  return disabledReason ? (
    // A disabled Button emits no hover, so a focusable span wraps it as the tooltip trigger.
    <Tooltip label={disabledReason} align="top">
      <span className={styles.saveTrigger} tabIndex={0}>
        {saveButton}
      </span>
    </Tooltip>
  ) : (
    saveButton
  );
}

/**
 * The collapsed Save `<MenuItem>` (the <1500px treatment, folded into the Actions menu).
 * Mirrors the button's state as far as a menu item can: dynamic label + disabled + the
 * "Saved" Checkmark. The disabled-reason tooltip isn't shown (menu items have none).
 *
 * @param {{ controller: SaveEvaluationController }} props - The resolved Save state.
 * @return {JSX.Element} The Save menu item.
 */
export function SaveEvaluationMenuItem({
  controller,
}: {
  controller: SaveEvaluationController;
}): JSX.Element {
  const { canSave, isSaving, saved, label, onSave } = controller;
  return (
    <MenuItem
      label={label}
      disabled={!canSave}
      renderIcon={saved && !isSaving ? Checkmark : undefined}
      onClick={onSave}
    />
  );
}
